/**
 * Demo command catalog — backing data + actions for the hidden Cmd+K
 * "Start / Reset / Exit demo" command.
 *
 * THROWAWAY DEMO CODE. Demo mode is a single global on/off switch
 * (`@/demo/core/demo-mode`) that affects EVERY demo at once. This module lists
 * each demo's fixture-reset so "Reset Demo" can re-seed them all, and exposes
 * the start/exit/reset actions the command calls. Adding a demo = one entry
 * here; deleting a demo = drop its entry (and its import).
 *
 * Start/Exit reload the page because the flag is read synchronously at module
 * load (mirrors the existing Exit buttons); Reset re-seeds in place.
 */

import { isDemoMode, setDemoMode } from '@/demo/core/demo-mode';
import { apDemoStore } from '@/demo/ap/fixture-store';
import { workflowRecoveryStore } from '@/demo/workflow-recovery/fixture-store';
import { resetArDemoState } from '@/demo/ar/fixture-store';
import { resetVoiceCall } from '@/demo/ar/voice-call-store';
import { resetPortal } from '@/demo/ar/portal-store';
import { resetImDemoState } from '@/demo/im/fixture-store';
import { homeInboxStore } from '@/demo/home/inbox-store';
import { homeTasksStore } from '@/demo/home/tasks-store';
import { resetProcurementDemo } from '@/demo/procurement/store';

export interface DemoCatalogEntry {
  /** Short key (matches the legacy demo key, e.g. 'ap'). */
  key: string;
  /** Short label shown in the "Start Demo" hint (e.g. 'AP', 'Workflows'). */
  label: string;
  /** Re-seed this demo's fixture store(s) to their initial snapshot. */
  reset: () => void;
}

export const DEMO_CATALOG: DemoCatalogEntry[] = [
  { key: 'ap', label: 'AP', reset: () => apDemoStore.reset() },
  { key: 'wf', label: 'Workflows', reset: () => workflowRecoveryStore.reset() },
  {
    key: 'ar',
    label: 'AR',
    reset: () => {
      resetArDemoState();
      resetVoiceCall();
      resetPortal();
    },
  },
  { key: 'im', label: 'Integrations', reset: resetImDemoState },
  {
    key: 'home',
    label: 'Home',
    reset: () => {
      homeInboxStore.reset();
      homeTasksStore.reset();
    },
  },
  { key: 'procurement', label: 'Procurement', reset: resetProcurementDemo },
];

/** True when demo mode is on. */
export function isDemoModeOn(): boolean {
  return isDemoMode();
}

/**
 * "Hidden": the demo command only surfaces once the user types something
 * containing "demo" (so it never pollutes normal entity/page search).
 */
export function matchesDemoTrigger(search: string): boolean {
  return search.trim().toLowerCase().includes('demo');
}

/** Turn the demo on (all demos) and reload to apply. */
export function startDemo(): void {
  setDemoMode(true);
  window.location.reload();
}

/** Turn the demo off and reload into the real app. */
export function exitDemo(): void {
  setDemoMode(false);
  window.location.reload();
}

/** Re-seed every demo's fixtures (caller invalidates queries). */
export function resetAllDemos(): void {
  DEMO_CATALOG.forEach((demo) => demo.reset());
}
